/**
 * A webinar's Invites card: each send, how many got it, opened it and saved a
 * seat, and who. The send's one reminder goes to the ones without a seat.
 */
import { useState } from "react";
import type { InviteeStatus, InviteSendView } from "../../shared/types";
import { ApiError, api } from "../lib/api";
import "../styles/invite.css";
import { dayNumber, formatAt } from "../lib/zones";

const fmt = (n: number): string => n.toLocaleString("en-US");

const STATUS: Record<InviteeStatus, string> = {
  seat: "Saved a seat",
  opened: "Opened",
  got: "Got it",
  waiting: "Sending",
  bounced: "Didn't reach them",
};

/** "Today, 4:12 PM", "Yesterday, 9:03 AM", or "Sep 12, 4:12 PM", in the workspace's zone. */
function sentWhen(at: number, now = Date.now()): string {
  const time = formatAt(at * 1000, { hour: "numeric", minute: "2-digit" });
  const ago = dayNumber(now) - dayNumber(at * 1000);
  if (ago === 0) return `Today, ${time}`;
  if (ago === 1) return `Yesterday, ${time}`;
  return `${formatAt(at * 1000, { month: "short", day: "numeric" })}, ${time}`;
}

export function InvitesCard({
  webinarId,
  sends,
  onChanged,
  onToast,
  canRemind,
}: {
  webinarId: string;
  sends: InviteSendView[];
  onChanged: (send: InviteSendView) => void;
  onToast: (message: string) => void;
  /** A live that has started or ended is past reminding. */
  canRemind: boolean;
}) {
  if (sends.length === 0) return null;
  return (
    <div className="card invcard">
      <div className="invcard-head">
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
        <h3>Invites</h3>
        <small>Each person got their own link</small>
      </div>
      {sends.map((s) => (
        <Send key={s.id} webinarId={webinarId} send={s} onChanged={onChanged} onToast={onToast} canRemind={canRemind} />
      ))}
    </div>
  );
}

function Send({
  webinarId,
  send: s,
  onChanged,
  onToast,
  canRemind,
}: {
  webinarId: string;
  send: InviteSendView;
  onChanged: (send: InviteSendView) => void;
  onToast: (message: string) => void;
  canRemind: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const pct = (n: number) => (s.total > 0 ? (n / s.total) * 100 : 0);

  const remind = () => {
    if (busy) return;
    setBusy(true);
    api.invites
      .remind(webinarId, s.id)
      .then((r) => {
        onChanged(r.send);
        onToast(`Reminded ${fmt(s.remindable)} ${s.remindable === 1 ? "person" : "people"}`);
      })
      .catch((err) => onToast(err instanceof ApiError ? err.message : "The reminder didn't go out. Try again."))
      .finally(() => setBusy(false));
  };

  return (
    <div className="invsend">
      <div className="invsend-who">
        <b>
          {sentWhen(s.at)} · {fmt(s.total)} {s.total === 1 ? "person" : "people"}
        </b>
        {s.audience ? <small>{s.audience}</small> : null}
      </div>
      <div className="invsend-stats">
        <div>
          <b>{fmt(s.got)}</b>
          <span>Got it</span>
        </div>
        <div>
          <b>{fmt(s.opened)}</b>
          <span>Opened</span>
        </div>
        <div>
          <b>{fmt(s.seats)}</b>
          <span>Saved a seat</span>
        </div>
      </div>
      <div className="invsend-bar" aria-hidden="true">
        <i className="seat" style={{ width: `${pct(s.seats)}%` }} />
        <i className="open" style={{ width: `${pct(s.opened - s.seats)}%` }} />
      </div>
      <div className="invsend-foot">
        <span className="invsend-key">
          <span>
            <i className="seat" />
            Saved a seat
          </span>
          <span>
            <i className="open" />
            Opened
          </span>
        </span>
        <span className="invsend-acts">
          {s.reminded_at !== null ? (
            <span>Reminded {sentWhen(s.reminded_at).replace(/^(Today|Yesterday)/, (m) => m.toLowerCase())}</span>
          ) : canRemind && s.remindable > 0 ? (
            <button type="button" className="ghost sm" disabled={busy} onClick={remind}>
              {busy ? "Reminding…" : `Remind the ${fmt(s.remindable)} without a seat`}
            </button>
          ) : null}
          <button type="button" className="ghost sm" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? "Hide who" : "See who"}
          </button>
        </span>
      </div>
      {open ? (
        <div className="invsend-people">
          {s.people.map((p) => (
            <div className="invsend-person" key={p.email}>
              <span>
                {p.name}
                {p.name !== p.email ? <small>{p.email}</small> : null}
              </span>
              <span className={`invst ${p.status}`}>{STATUS[p.status]}</span>
            </div>
          ))}
          {s.total > s.people.length ? (
            <div className="invsend-person">
              <span className="inv-quiet">and {fmt(s.total - s.people.length)} more</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
